import { useState } from "react";
import { Plus, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useApi, errorText } from "@/lib/api";
import CategoryPicker from "@/components/ledger/CategoryPicker";
import { checkRow, outsideFy, cellInput } from "@/components/ledger/rowRules";

const EMPTY = { description: "", received: "", spent: "", category: null, notes: "", for_whom: "", what: "", project: "" };
const EXTRA = [["for_whom", "For"], ["what", "What"], ["project", "Project"]];

export default function QuickAddRow({ fy, categories, onAdded }) {
  const api = useApi();
  const [draft, setDraft] = useState({ date: "", ...EMPTY });
  const [error, setError] = useState("");
  const set = (k) => (e) => setDraft((d) => ({ ...d, [k]: e.target.value }));

  const add = async () => {
    const { payload, error: err } = checkRow(draft);
    if (err) return setError(err);
    try {
      onAdded(await api("post", "/transactions", { ...payload, fy: fy.label }));
      setDraft((d) => ({ date: d.date, ...EMPTY }));
      setError("");
      toast.success("Row added");
    } catch (e) { setError(errorText(e)); }
  };
  const onKey = (e) => { if (e.key === "Enter") { e.preventDefault(); add(); } };
  const outside = outsideFy(draft.date, fy);
  const inputBase = `${cellInput} border-border bg-background`;

  return (
    <>
      <tr className="bg-emerald-500/[0.04] border-b border-border" data-testid="ledger-quick-add-row" onKeyDown={onKey}>
        <td className="pl-3"><Plus className="h-4 w-4 text-emerald-400" /></td>
        <td className="py-2">
          <div className="flex items-center">
            <input type="date" className={`${inputBase} font-mono`} value={draft.date} onChange={set("date")} data-testid="quick-add-date" />
            {outside && <AlertTriangle className="h-4 w-4 text-amber-400 shrink-0" aria-label="outside FY" data-testid="quick-add-outside-fy" />}
          </div>
        </td>
        <td />
        <td className="py-2"><input className={inputBase} placeholder="Description" value={draft.description} onChange={set("description")} data-testid="quick-add-description" /></td>
        <td className="py-2"><input className={`${inputBase} font-mono text-right`} placeholder="Received" inputMode="decimal" value={draft.received} onChange={set("received")} data-testid="quick-add-received" /></td>
        <td className="py-2"><input className={`${inputBase} font-mono text-right`} placeholder="Spent" inputMode="decimal" value={draft.spent} onChange={set("spent")} data-testid="quick-add-spent" /></td>
        <td className="py-2 px-1"><CategoryPicker value={draft.category} categories={categories} testId="quick-add-category" onChange={(v) => setDraft((d) => ({ ...d, category: v }))} /></td>
        <td className="py-2"><input className={inputBase} placeholder="Notes" value={draft.notes} onChange={set("notes")} data-testid="quick-add-notes" /></td>
        {EXTRA.map(([k, l]) => (
          <td key={k} className="py-2"><input className={inputBase} placeholder={l} value={draft[k]} onChange={set(k)} data-testid={`quick-add-${k.replace("_", "-")}`} /></td>
        ))}
        <td className="pr-2 text-right"><Button size="sm" onClick={add} data-testid="quick-add-submit">Add</Button></td>
      </tr>
      {(error || outside) && (
        <tr><td colSpan={12} className="px-4 py-1.5 text-xs">
          {error && <span className="text-rose-400 mr-4" data-testid="quick-add-error">{error}</span>}
          {outside && <span className="text-amber-300">Date is outside {fy.label} — the row will still be filed under {fy.label}.</span>}
        </td></tr>
      )}
    </>
  );
}
